/* eslint-disable react/no-multi-comp */
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from '@customink/pigment-react';
import {
  useGroupedPrice,
  useProject,
} from '@customink/project-service-react-components';
import type { ProjectItem } from '@customink/project-service-react-components';
import type { TURN_TIMES, TurnTime } from '@customink/project-service-types';
import type { ChangeEvent, InputHTMLAttributes } from 'react';
import { useState } from 'react';

import ShippingOptionLabel from './ShippingOptionLabel';
import { addFulfillment, shippingCopy } from './util';

import { useUpdateItems } from '@/hooks/ProjectItem';
import { colorPurple } from '@/utils/designTokens';
import { fireEvent } from '@/utils/metrics';
import { shippingDeliveryOption } from '@/utils/shippingDeliveryOption';

const TurnAdjustmentMessage = () => (
  <Typography marginBottom="4px" variant="uiTextTiny">
    <span style={{ color: colorPurple }}>*</span> Delivery dates have been
    adjusted due to holidays.
  </Typography>
);

type TurnTimeSelectProps = {
  designCid?: string;
};

export default function TurnTimeSelect({ designCid }: TurnTimeSelectProps) {
  const quote = useGroupedPrice(designCid);
  const { data: project } = useProject();

  const projectItems = designCid
    ? (project?.items || []).filter((item) => item.design?.cid === designCid)
    : project?.items || [];

  const defaultTurnTime: TurnTime = shippingDeliveryOption(projectItems || []);
  const [turnTime, setTurnTime] = useState<TurnTime>(defaultTurnTime);
  const [updateProjectItems] = useUpdateItems();

  const onSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const turnTimeIndex = project?.items?.findIndex(
      (item: ProjectItem) => item.design?.cid === designCid,
    );
    const firstOrNot = turnTimeIndex === 0 ? 'first' : 'not-first';
    type RushSuperStandard = Exclude<TurnTime, typeof TURN_TIMES.QUICKER>;
    const selectedTurnTime = event.target.defaultValue as RushSuperStandard;
    fireEvent('delivery_options', selectedTurnTime.toLowerCase(), firstOrNot);

    setTurnTime(selectedTurnTime);
    updateProjectItems(addFulfillment(projectItems, selectedTurnTime));
  };

  if (!quote) {
    return (
      <Skeleton
        data-testid="ship-to-one-loading"
        height="100%"
        sx={{ mt: '0.25rem' }}
        variant="rounded"
        width="95%"
      />
    );
  }

  const hasAdjustment = !!quote.turn_adjustment;

  return (
    <FormControl data-testid="ship-to-one" sx={{ width: '100%' }}>
      <RadioGroup
        aria-labelledby="radio-buttons-turn-time"
        data-testid="turn-time-radio-group"
        name="radio-buttons-turn-time"
        value={turnTime}
      >
        {shippingCopy.map((shippingOption) => {
          const key = shippingOption.key;
          const testkey = key.replaceAll('_', '-'); // rush_shipping -> rush-shipping

          return (
            !!quote?.[key] && (
              <FormControlLabel
                control={
                  <Radio
                    inputProps={
                      {
                        'data-testid': `turn-time-${testkey}`,
                      } as InputHTMLAttributes<HTMLInputElement> // cast to avoid type error
                    }
                    onChange={onSelect}
                  />
                }
                key={key}
                label={
                  <ShippingOptionLabel
                    quote={quote}
                    shippingOption={shippingOption}
                  />
                }
                multiline
                value={shippingOption.value}
              />
            )
          );
        })}
      </RadioGroup>
      {hasAdjustment && <TurnAdjustmentMessage />}
    </FormControl>
  );
}
